import { useState, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { usePerformanceAppraisals } from "./hooks/usePerformanceData";
import EditSelfRatingDialog from "./EditSelfRatingDialog";
import EditManagerRatingDialog from "./EditManagerRatingDialog";
import { useAppraisalState } from "./appraisal/hooks/useAppraisalState";
import { useTeamManagement } from "./appraisal/hooks/useTeamManagement";
import { useGoalManagement } from "./appraisal/hooks/useGoalManagement";
import { AppraisalMetricsCards } from "./appraisal/components/AppraisalMetricsCards";
import { GoalMetricsCards } from "./appraisal/components/GoalMetricsCards";
import AppraisalHeader from "./appraisal/components/AppraisalHeader";
import TabNavigation from "./appraisal/components/TabNavigation";
import TeamMemberSelector from "./appraisal/components/TeamMemberSelector";
import GoalsTable from "./appraisal/components/GoalsTable";
import { useSupervisorSync } from "./hooks/useSupervisorSync";

const PerformanceAppraisalTab = () => {
  const [activeTab, setActiveTab] = useState("personal-goals");
  const [selectedTeamMember, setSelectedTeamMember] = useState("");
  
  const { teamMembers, isLoadingMembers, userIsSupervisor } = useTeamManagement();
  const { personalGoals, selectedMemberGoals, isLoadingMemberGoals } = useGoalManagement(selectedTeamMember);
  const { data: appraisals = [], isLoading, error } = usePerformanceAppraisals();

  // Apply supervisor sync for both personal goals and selected member goals
  useSupervisorSync(personalGoals || [], undefined);
  useSupervisorSync(selectedMemberGoals || [], selectedTeamMember);

  const appraisalState = useAppraisalState(
    personalGoals, 
    selectedMemberGoals, 
    activeTab, 
    teamMembers, 
    userIsSupervisor
  );

  useEffect(() => {
    if (teamMembers.length > 0 && !selectedTeamMember) {
      setSelectedTeamMember(teamMembers[0].id);
    }
  }, [teamMembers, selectedTeamMember]);

  const isViewingOwnGoals = (): boolean => {
    return activeTab === "personal-goals";
  };

  const isSupervisorViewingTeamMember = (): boolean => {
    return activeTab === "manage-team-members" && userIsSupervisor && selectedTeamMember !== "";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse">Loading performance appraisals...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-400">Error loading performance appraisals</div>
      </div>
    );
  }

  const currentGoals = activeTab === "personal-goals" ? personalGoals : selectedMemberGoals;
  const totalGoals = currentGoals?.length || 0;
  const activeGoals = currentGoals?.filter(goal => goal.status === 'active').length || 0;
  const completedGoals = currentGoals?.filter(goal => goal.status === 'completed').length || 0;
  const averageProgress = currentGoals && currentGoals.length > 0 
    ? Math.round(currentGoals.reduce((sum, goal) => sum + goal.current_progress, 0) / currentGoals.length) 
    : 0;

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">Performance Appraisal</h2>
          {appraisalState.supervisorName && (
            <div className="flex items-center gap-2 text-white text-[11px]">
              <span>Supervisor:</span>
              <Badge variant="secondary" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                {appraisalState.supervisorName}
              </Badge>
            </div>
          )}
        </div>
        
        {!userIsSupervisor && (
          <div className="border border-purple-500/30 rounded-lg p-3">
            <p className="text-white text-[13px]">
              For team members: Goals are loaded by your direct supervisor. Kindly contact your supervisor if you have any questions.
            </p>
          </div>
        )}

        {userIsSupervisor && (
          <TabNavigation 
            activeTab={activeTab} 
            onTabChange={setActiveTab} 
          />
        )}

        <AppraisalMetricsCards 
          totalAppraisals={appraisalState.appraisalMetrics.totalAppraisals}
          submittedAppraisals={appraisalState.appraisalMetrics.selfRatingDone}
          managerRatingDoneAppraisals={appraisalState.appraisalMetrics.managerRatingDone}
          completedAppraisals={0}
          isVisible={activeTab === "manage-team-members" && userIsSupervisor}
        />

        <TeamMemberSelector
          teamMembers={teamMembers}
          selectedTeamMember={selectedTeamMember}
          onTeamMemberChange={setSelectedTeamMember}
          isVisible={activeTab === "manage-team-members" && userIsSupervisor}
        />

        <GoalMetricsCards 
          totalGoals={totalGoals}
          activeGoals={activeGoals}
          averageProgress={averageProgress}
          completedGoals={completedGoals}
        />

        <GoalsTable
          goals={currentGoals}
          goalSelfRatings={appraisalState.goalSelfRatings}
          goalManagerRatings={appraisalState.goalManagerRatings}
          goalComments={appraisalState.goalComments}
          goalManagerComments={appraisalState.goalManagerComments}
          savedGoals={appraisalState.savedGoals}
          savedManagerGoals={appraisalState.savedManagerGoals}
          isViewingOwnGoals={isViewingOwnGoals()}
          isSupervisorViewingTeamMember={isSupervisorViewingTeamMember()}
          goalAppraisals={appraisalState.goalAppraisals}
          onSelfRatingChange={appraisalState.handleSelfRatingChange}
          onManagerRatingChange={appraisalState.handleManagerRatingChange}
          onEditSelfRating={appraisalState.handleEditSelfRating}
          onEditManagerRating={appraisalState.handleEditManagerRating}
        />

        <EditSelfRatingDialog
          isOpen={appraisalState.editDialogOpen}
          onClose={() => appraisalState.setEditDialogOpen(false)}
          goalId={appraisalState.selectedGoal?.id || ""}
          goalTitle={appraisalState.selectedGoal?.goal_title || ""}
          targetValue={appraisalState.selectedGoal?.target_value || ""}
          selfRating={appraisalState.selectedGoal ? appraisalState.goalSelfRatings[appraisalState.selectedGoal.id] || "" : ""}
          onSaveSuccess={appraisalState.handleSaveSuccess}
        />

        <EditManagerRatingDialog
          isOpen={appraisalState.editManagerDialogOpen}
          onClose={() => appraisalState.setEditManagerDialogOpen(false)}
          goalId={appraisalState.selectedGoal?.id || ""}
          goalTitle={appraisalState.selectedGoal?.goal_title || ""}
          targetValue={appraisalState.selectedGoal?.target_value || ""}
          managerRating={appraisalState.selectedGoal ? appraisalState.goalManagerRatings[appraisalState.selectedGoal.id] || "" : ""}
          onSaveSuccess={appraisalState.handleManagerSaveSuccess}
        />
      </div>
    </TooltipProvider>
  );
};

export default PerformanceAppraisalTab;
